<script lang="ts" setup>
import { useLanguage } from '#composables/useLanguage';
import { formatIndicatorValue, secureMathRandom } from '#helpers';
import type { Indicator, UnitType } from '@carrefour-gcs/shared';
import type { EChartsOption, SeriesOption } from 'echarts';
import { BarChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { registerTheme, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { computed, onBeforeMount } from 'vue';
import VChart from 'vue-echarts';
import { LINKS_GROWTH_THEME } from './constants';

const props = defineProps<{
  ordinates: NonNullable<Indicator['ordinates']>;
  abscissae: NonNullable<Indicator['abscissae']>;
  type: 'values' | 'comparisons';
}>();

// --- Instantiate composables.
const { t, language } = useLanguage();

// --- Register the echarts components and theme.
onBeforeMount(() => {
  use([CanvasRenderer, BarChart, TooltipComponent, LegendComponent, GridComponent]);
  registerTheme('linksGrowth', LINKS_GROWTH_THEME);
});

const hasNoData = computed(() =>
  props.ordinates
    .flatMap((ordinate) => (props.type === 'values' ? ordinate.values : ordinate.comparisons?.values))
    .every((item) => item === null)
);

const option = computed(() => {
  const legend = props.ordinates.map((value) => t(value.name));

  const unit = computed<UnitType>(() => {
    const firstOrdinate = props.ordinates[0];
    if (!firstOrdinate) {
      return 'integer';
    }
    return (props.type === 'values' ? firstOrdinate.unit : firstOrdinate.comparisons?.unit) ?? 'integer';
  });

  const valueFormatter = (value: number) =>
    formatIndicatorValue({
      indicator: { value, unit: unit.value },
      withPlusSign: true,
      language: language.value,
      isChart: true
    });

  // --- Build the `xAxis.data` values.
  const abscissae = props.abscissae.map((value) => t(value));

  // --- Build the `echarts` series.
  const series = props.ordinates.map<SeriesOption>((value) => {
    const values = props.type === 'values' ? value.values : value.comparisons?.values;

    return {
      name: t(value.name),
      type: 'bar',
      label: {
        show: !(values && values.length > 5),
        position: 'insideTop',
        formatter: (params) => {
          if (typeof params.value !== 'number') {
            return '';
          }

          return valueFormatter(params.value);
        }
      },
      data: values,
      smooth: true,
      animationDuration: 3000,
      animationEasing: 'sinusoidalInOut'
    };
  });

  const noDataSeries = Array.from(
    { length: props.ordinates?.length ? props.ordinates?.length : 3 },
    () => secureMathRandom() * 100
  ).map<SeriesOption>((_, index) => {
    return {
      name: props.ordinates[index] ? t(props.ordinates[index].name) : undefined,
      type: 'bar',
      data: Array.from({ length: abscissae.length }, () => secureMathRandom() * 100),
      smooth: true,
      animationDuration: 3000,
      animationEasing: 'sinusoidalInOut'
    };
  });

  return {
    series: hasNoData.value ? noDataSeries : series,
    tooltip: {
      show: !hasNoData.value,
      trigger: 'item',
      valueFormatter
    },
    legend: {
      bottom: 0,
      left: 0,
      data: legend
    },
    grid: {
      top: 15,
      right: 15,
      left: 15,
      bottom: 25,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: abscissae
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: valueFormatter,
        valueFormatter: valueFormatter
      }
    }
  } as EChartsOption;
});
</script>

<template>
  <div class="relative">
    <VChart
      class="w-full h-64"
      :class="{
        grayscale: hasNoData
      }"
      :option="option"
      theme="linksGrowth"
      autoresize
    />

    <div v-if="hasNoData" class="absolute top-0 left-0 h-full w-full flex items-center justify-center">
      <div class="bg-white p-5">
        <div class="font-bold">{{ t('chart.noData.title') }}</div>
        <div>{{ t('chart.noData.content') }}</div>
      </div>
    </div>
  </div>
</template>
